import { useEffect, useState } from 'react';
import { Box, Button, Divider, IconButton, Modal, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from 'react-query';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { client } from '@frontend/features/common/services';
import { StandardSmallModalBox } from '@frontend/features/common/utils/modals';
import { useGetFieldBlockById } from '../../queries';

interface Props {
  open: boolean;
  fieldBlockId: string;
  agencyId: string;
  onClose: () => void;
}

interface CreateFieldDTO {
  name: string;
}

const resolver = yupResolver(
  yup.object().shape({
    name: yup.string().required(),
  }),
);

export const EditFieldBlockModal = ({ open, onClose, fieldBlockId, agencyId }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useGetFieldBlockById({
    id: fieldBlockId,
  });

  const methods = useForm({
    resolver,
  });
  const { handleSubmit, control, setValue } = methods;

  const onSubmit = async (formData: CreateFieldDTO) => {
    setLoading(true);
    try {
      await client.patch(`custom-fields/field-block/${fieldBlockId}`, { ...formData, agencyId });
      addToast({
        message: t('customFields.fieldBlockCreate.editSuccess'),
        color: 'success',
      });
    } catch (e) {
      addToast({
        message: t('customFields.fieldBlockCreate.editError'),
        color: 'error',
      });

      // TODO: esto tendria q ser un sentry emit;
      console.log(e);
    } finally {
      setLoading(false)
      queryClient.invalidateQueries();
      onClose();
    }
  };

  useEffect(() => {
    if (data) {
      setValue('name', data.name || '', { shouldDirty: true });
    }
  }, [data, setValue]);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="edit-fieldblocks-modal-title">
      <StandardSmallModalBox display="flex" flexDirection="column" gap={theme.spacing(2)}>
        <Box display="flex" gap={theme.spacing(2)}>
          <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
            <Typography id="edit-fieldblocks-modal-title" variant="h6" component="h2">
              {t('customFields.fieldBlockEdit.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </Box>
        <Divider />
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              display="grid"
              gridTemplateColumns="repeat(12, 1fr)"
              gap={2}
              sx={{
                maxHeight: 'calc(100vh - 300px)',
                overflowX: 'auto',
                paddingTop: theme.spacing(2),
              }}
            >
              <Box gridColumn="span 12">
                <ControlledTextField
                  control={control}
                  name="name"
                  label={t('customFields.fieldBlockCreate.name')}
                  placeholder={t('common.form.onlyLetters')}
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                />
              </Box>

            </Box>
            <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
              <Divider />
              <Box
                display="flex"
                justifyContent="space-evenly"
                width="100%"
                gap={theme.spacing(1)}
                sx={{
                  '& > button': {
                    width: 'calc(50% - 4px)',
                  },
                }}
              >
                <Button variant="outlined" color="error" size="medium" onClick={onClose}>
                  {t('common.form.discard')}
                </Button>
                <LoadingButton loading={loading} disabled={loading} variant="contained" color="primary" type="submit" size="medium">
                  {t('common.form.save')}
                </LoadingButton>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </StandardSmallModalBox>
    </Modal>
  );
};
